import Button from 'components/common/buttons/Button';
import { Lightning } from '@lightningjs/sdk';
import { constants } from 'aliases';
import { translate } from 'support/translate';
import { HoverableComponent } from 'components/common/HoverableComponent';
import EmailInputField from '../common/EmailInputField';
import TextLabel from 'components/common/TextLabel';

export interface EmailInputSectionTemplateSpec
  extends Lightning.Component.TemplateSpec {
  Title: typeof TextLabel;
  Email: {
    Description: typeof TextLabel;
    EmailInput: typeof EmailInputField;
  };
  ContinueButton: typeof Button;
}

const WIDTH = 684;
const HEIGHT = 522;

const TITLE_FONT_SIZE = 36;
const TITLE_FONT_STYLE = 'italic bold';

const EMAIL_OFFSET_Y = 142;
const EMAIL_DESC_FONT_SIZE = 22;
const EMAIL_DESC_LINE_HEIGHT = 35;

const BUTTON_HEIGHT = 65;
const BUTTON_FONT_SIZE = 18;

export default class EmailInputSection extends HoverableComponent<EmailInputSectionTemplateSpec> {
  private _Title = this.getByRef('Title')!;
  private _Email = this.getByRef('Email')!;
  private _Description = this._Email.getByRef('Description')!;
  private _ContinueButton = this.getByRef('ContinueButton')!;
  private _EmailInput = this._Email.getByRef('EmailInput')!;

  get announce() {
    return [this._Title.text?.text, this._Description.text?.text];
  }

  static override _template(): Lightning.Component.Template<EmailInputSectionTemplateSpec> {
    return {
      w: WIDTH,
      h: HEIGHT,
      flex: {
        direction: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      Title: {
        type: TextLabel,
        w: WIDTH, // Italic font gets cut off without a larger than automatic width
        text: {
          text: translate('account.signin.remoteTitle'),
          textAlign: 'center',
          fontSize: TITLE_FONT_SIZE,
          fontStyle: TITLE_FONT_STYLE,
        },
      },
      Email: {
        y: EMAIL_OFFSET_Y,
        flex: { direction: 'column' },
        flexItem: false,
        Description: {
          type: TextLabel,
          text: {
            text: translate('account.signin.remoteDescription'),
            wordWrapWidth: WIDTH,
            lineHeight: EMAIL_DESC_LINE_HEIGHT,
            fontSize: EMAIL_DESC_FONT_SIZE,
            maxLines: 3,
          },
        },
        EmailInput: {
          type: EmailInputField,
          passSignals: {
            $onHover: '$onHover',
            $emailInputSelected: '$openKeyboard',
          },
          signals: { $onHover: '$onHover' },
          zIndex: constants.ui.zIndex.foregroundLayer + 2,
        },
      },
      ContinueButton: {
        type: Button,
        label: translate('account.signin.continue'),
        minWidth: WIDTH,
        height: BUTTON_HEIGHT,
        fontSize: BUTTON_FONT_SIZE,
        action: '$onEmailSubmit',
        zIndex: constants.ui.zIndex.foregroundLayer + 2,
        signals: {
          $onEmailSubmit: '$onEmailSubmit',
          $onHover: '$onHover',
        },
      },
    };
  }

  override _onActive() {
    this._setState('Email');
  }

  private async $onEmailSubmit() {
    const isValid = await this._EmailInput.checkValid();
    if (isValid) {
      this.signal('$onEmailSubmit', this._EmailInput.input);
    }
  }

  private $onHover(target: any) {
    let state = null;

    if (target.id === this._EmailInput.id) {
      state = 'Email';
    } else if (target.id === this._ContinueButton.id) {
      state = 'ContinueButton';
    }

    if (state) {
      this._setState(state);
      this.signal('$onEmailInputHovered');
    }
  }

  static override _states() {
    return [
      class Email extends this {
        override _handleDown() {
          this._setState('ContinueButton');
        }

        override _getFocused() {
          return this._EmailInput;
        }
      },
      class ContinueButton extends this {
        override _handleUp() {
          this._setState('Email');
        }
        override _getFocused() {
          return this._ContinueButton;
        }
      },
    ];
  }
}
